import { FC, ReactNode } from 'react';

import {
  BodyTypography,
  FontSizeEnum,
  FontWeightEnum,
  FormContainer,
  FormInput,
  Heading,
  HeadingEnum,
  Label,
  Link,
  FormFileUpload,
  MessageTypes,
  Message,
  FormTextArea,
  FormCheckbox,
} from '@argenta/react-ui';
import { t } from '@lingui/macro';
import { Trans } from '@lingui/react';

import { getFormContainerI18nStrings } from './ApplicationForm.strings';
import { ApplicationFormProps, ApplicationFormValues } from './ApplicationForm.types';
import {
  ALLOWED_FILE_TYPE_EXTENSIONS,
  getApplicationFormValidation,
} from './ApplicationForm.validation';

import './ApplicationForm.styles.scss';

export const ApplicationForm: FC<ApplicationFormProps> = ({
  isLoading,
  errorMessage,
  onSubmit,
}) => {
  const initialValues: ApplicationFormValues = {
    firstName: '',
    lastName: '',
    email: '',
    linkedIn: '',
    cvAgreement: false,
  };

  const renderForm = (): ReactNode => (
    <div className="argnt-vacancies-application-form__content">
      <div>
        <Heading type={HeadingEnum.H3} fontWeight={FontWeightEnum.LIGHT} as="h2">
          <Trans id="vacancies.application.form.personDetails" values={{}} components={{}} />
        </Heading>
        {errorMessage && <Message type={MessageTypes.Error}>{errorMessage}</Message>}
        <Label htmlFor="firstName-id">
          {t({ id: 'vacancies.application.form.label.firstName' })}
        </Label>
        <FormInput name="firstName" id="firstName-id" maxLength={50} />
      </div>
      <div>
        <Label htmlFor="lastName-id">
          {t({ id: 'vacancies.application.form.label.lastName' })}
        </Label>
        <FormInput name="lastName" id="lastName-id" maxLength={50} />
      </div>
      <div>
        <Label htmlFor="email-id">{t({ id: 'vacancies.application.form.label.email' })}</Label>
        <FormInput name="email" id="email-id" maxLength={72} />
      </div>
      <div>
        <Label htmlFor="linkedIn-id">
          <Trans id="vacancies.application.form.label.linkedIn" values={{}} components={{}} />
        </Label>
        <FormInput
          name="linkedIn"
          id="linkedIn-id"
          placeholder={t({ id: 'vacancies.application.form.placeholder.linkedIn' })}
          maxLength={130}
        />
      </div>
      <div>
        <Label htmlFor="motivation-id">
          <Trans id="vacancies.application.form.label.motivation" components={{}} values={{}} />
        </Label>
        <FormTextArea
          name="motivation"
          className="argnt-vacancies-application-form__motivation-textarea"
          placeholder={t({ id: 'vacancies.application.form.placeholder.motivation' })}
          getI18nCharactersLeft={(charactersLeft) =>
            t({ id: 'vacancies.application.form.charactersLeft', values: { charactersLeft } })
          }
          maxLength={1000}
          rows={6}
          id="motivation-id"
        />
      </div>
      <div>
        <Heading
          type={HeadingEnum.H3}
          fontWeight={FontWeightEnum.LIGHT}
          className="argnt-vacancies-application-form__attachments-heading"
          as="h2"
        >
          <Trans id="vacancies.application.form.attachments" values={{}} components={{}} />
        </Heading>
        <Label htmlFor="cv-id">{t({ id: 'vacancies.application.form.label.cv' })}</Label>
        <FormFileUpload
          id="cv-id"
          name="cv"
          accept={ALLOWED_FILE_TYPE_EXTENSIONS}
          i18nStrings={{
            buttonLabel: t({ id: 'vacancies.application.form.fileUpload.buttonLabel' }),
            noFileLabel: t({ id: 'vacancies.application.form.fileUpload.noFileLabel' }),
          }}
        />
      </div>
      <FormCheckbox
        name="cvAgreement"
        id="cvAgreement"
        label=""
        subtitle={t({ id: 'vacancies.application.form.cvAgreement.label' })}
      />
      <BodyTypography fontSize={FontSizeEnum.BASE} fontWeight={FontWeightEnum.REGULAR}>
        <Trans
          id="vacancies.application.form.legalParagraph"
          components={[
            <br />,
            <Link
              href={t({ id: 'vacancies.application.form.privacyPolicy.href' })}
              target="_blank"
            />,
          ]}
          values={{}}
        />
      </BodyTypography>
    </div>
  );

  return (
    <div className="argnt-vacancies-application-form">
      <FormContainer<ApplicationFormValues>
        onSubmit={onSubmit}
        i18nStrings={getFormContainerI18nStrings()}
        initialValues={initialValues}
        validation={getApplicationFormValidation()}
        loading={isLoading}
      >
        {renderForm}
      </FormContainer>
    </div>
  );
};
